// routes
import { paths } from 'src/routes/paths';
// utils
import axios from 'src/utils/axios';

// ----------------------------------------------------------------------

function jwtDecode(token) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join('')
  );

  return JSON.parse(jsonPayload);
}

// ----------------------------------------------------------------------

export const isValidToken = (accessToken) => {
  if (!accessToken) {
    return false;
  }

  const decoded = jwtDecode(accessToken);

  const currentTime = Date.now() / 1000;

  return decoded.exp > currentTime;
};

// ----------------------------------------------------------------------

// setTimeout's `delay` is a signed 32-bit int. Anything larger overflows
// and the callback fires immediately. Our admin JWTs are 30 days, which
// blows past this and was causing every fresh login to show "Token
// expired" and bounce back to /auth/login.
const MAX_TIMEOUT_MS = 2147483647; // ~24.85 days

export const tokenExpired = (exp) => {
  // eslint-disable-next-line prefer-const
  let expiredTimer;

  const currentTime = Date.now();
  const timeLeft = exp * 1000 - currentTime;

  clearTimeout(expiredTimer);

  // If the token is already past its exp, log out immediately. Otherwise
  // arm the alert at the real expiry, capped to setTimeout's max — when
  // the tab is reloaded sooner (the common case for long-lived tokens),
  // setSession re-arms with a fresh delay.
  if (timeLeft <= 0) {
    localStorage.removeItem('accessToken');
    window.location.href = paths.auth.login;
    return;
  }

  expiredTimer = setTimeout(() => {
    // Re-check at fire time — if we capped the delay, the token may still
    // be valid (the tab just stayed open past the cap). Re-arm in that
    // case instead of bouncing the user.
    if (Date.now() < exp * 1000) {
      tokenExpired(exp);
      return;
    }
    alert('Token expired');
    localStorage.removeItem('accessToken');
    window.location.href = paths.auth.login;
  }, Math.min(timeLeft, MAX_TIMEOUT_MS));
};

// ----------------------------------------------------------------------

export const setSession = (accessToken) => {
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken);

    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

    // This function below will handle when token is expired
    const { exp } = jwtDecode(accessToken); // ~3 days by minimals server
    tokenExpired(exp);
  } else {
    localStorage.removeItem('accessToken');

    delete axios.defaults.headers.common.Authorization;
  }
};
